import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";
import AppShell from "@/components/AppShell";
import { api, formatApiError } from "@/lib/api";
import { ORG_TYPES, CERTIFICATIONS } from "@/lib/constants";

const empty = {
  org_name: "", org_type: "business", industry: "", description: "",
  location: "", looking_for: "", offerings: "", certifications: [],
  website: "", employee_count: "", avatar_url: "",
};

function Field({ label, children, hint }) {
  return (
    <div>
      <label className="text-sm font-medium text-slate-700">{label}</label>
      {hint && <p className="text-xs text-slate-400">{hint}</p>}
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

const inputCls = "w-full h-11 px-3 rounded-md border border-slate-300 focus:ring-2 focus:ring-[#2563EB] focus:border-[#2563EB] outline-none";

export default function ProfilePage() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadProfile = useCallback(() => {
    api.get("/profiles/me").then(({ data }) => {
      if (data && data.org_name) setForm({ ...empty, ...data });
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleCert = (c) =>
    setForm((f) => ({
      ...f,
      certifications: f.certifications.includes(c)
        ? f.certifications.filter((x) => x !== c)
        : [...f.certifications, c],
    }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        org_name: form.org_name, org_type: form.org_type, industry: form.industry,
        description: form.description, location: form.location, looking_for: form.looking_for,
        offerings: form.offerings, certifications: form.certifications,
        website: form.website, employee_count: form.employee_count, avatar_url: form.avatar_url,
      };
      const { data } = await api.post("/profiles", payload);
      setForm({ ...empty, ...data });
      toast.success("Profile saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <AppShell title="Business Profile"><div className="flex justify-center py-20"><Loader2 className="animate-spin text-slate-400" /></div></AppShell>;
  }

  return (
    <AppShell title="Business Profile" subtitle="The richer your profile, the better your AI matches.">
      <form onSubmit={save} className="max-w-3xl space-y-6">
        <div className="rounded-xl border border-slate-200 bg-white p-6 space-y-5">
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Organization name">
              <input className={inputCls} required value={form.org_name} onChange={(e) => set("org_name", e.target.value)} data-testid="profile-orgname-input" placeholder="Acme Industrial Supplies" />
            </Field>
            <Field label="Organization type">
              <select className={inputCls} value={form.org_type} onChange={(e) => set("org_type", e.target.value)} data-testid="profile-orgtype-select">
                {ORG_TYPES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </Field>
            <Field label="Industry">
              <input className={inputCls} required value={form.industry} onChange={(e) => set("industry", e.target.value)} data-testid="profile-industry-input" placeholder="Manufacturing" />
            </Field>
            <Field label="Location">
              <input className={inputCls} required value={form.location} onChange={(e) => set("location", e.target.value)} data-testid="profile-location-input" placeholder="Austin, TX" />
            </Field>
            <Field label="Website">
              <input className={inputCls} value={form.website} onChange={(e) => set("website", e.target.value)} data-testid="profile-website-input" placeholder="https://" />
            </Field>
            <Field label="Company size">
              <input className={inputCls} value={form.employee_count} onChange={(e) => set("employee_count", e.target.value)} data-testid="profile-size-input" placeholder="e.g. 11-50" />
            </Field>
          </div>
          <Field label="About your business">
            <textarea className={`${inputCls} h-24 py-2 resize-none`} required value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="profile-description-input" placeholder="What you do and what makes you stand out." />
          </Field>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-6 space-y-5">
          <h3 className="font-heading font-bold text-[#0A0F1C]">Matching signals</h3>
          <Field label="What you're looking for" hint="Funding, contracts, suppliers, partners, customers…">
            <textarea className={`${inputCls} h-20 py-2 resize-none`} value={form.looking_for} onChange={(e) => set("looking_for", e.target.value)} data-testid="profile-lookingfor-input" placeholder="Enterprise buyers seeking industrial components and logistics." />
          </Field>
          <Field label="What you offer" hint="Products, services, capital, opportunities you provide.">
            <textarea className={`${inputCls} h-20 py-2 resize-none`} value={form.offerings} onChange={(e) => set("offerings", e.target.value)} data-testid="profile-offerings-input" placeholder="Industrial components, fulfillment, logistics." />
          </Field>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-6">
          <h3 className="font-heading font-bold text-[#0A0F1C]">Certifications</h3>
          <p className="text-xs text-slate-400 mt-0.5">Select any certifications your business holds.</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {CERTIFICATIONS.map((c) => {
              const on = form.certifications.includes(c.value);
              return (
                <button type="button" key={c.value} onClick={() => toggleCert(c.value)} data-testid={`cert-${c.value}`}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${on ? "bg-[#0A0F1C] text-white border-[#0A0F1C]" : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"}`}>
                  {c.label}
                </button>
              );
            })}
          </div>
        </div>

        <button type="submit" disabled={saving} data-testid="profile-save-button"
          className="h-11 px-6 rounded-md bg-[#2563EB] text-white font-medium flex items-center gap-2 hover:bg-[#1D4ED8] transition-colors disabled:opacity-60">
          {saving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />} Save profile
        </button>
      </form>
    </AppShell>
  );
}
